/**
 * Custom product gallery: horizontal scroll track + thumbnails.
 *
 * After injecting HTML (post-load), run ONE of:
 *   1) window.CustomGallery && CustomGallery.init(galleryElementOrSelector);
 *   2) Poll until CustomGallery exists (snippet below) if your code may run before custom-gallery.js.
 *   3) document.dispatchEvent(new CustomEvent('custom-gallery:attach', { detail: { gallery: el } }));
 *
 * Paste helper (works even if this file loads slightly after your code):
 *   function customGalleryBind(el) {
 *     if (!el) return;
 *     function run() { window.CustomGallery.init(el); }
 *     if (window.CustomGallery) { run(); return; }
 *     var n = 0, t = setInterval(function () {
 *       n++; if (window.CustomGallery) { clearInterval(t); run(); }
 *       else if (n > 200) clearInterval(t);
 *     }, 50);
 *   }
 */
(function () {
  'use strict';

  /**
   * @param {HTMLElement} gallery
   * @returns {function(): void} destroy
   */
  function bindGallery(gallery) {
    var track = gallery.querySelector('.custom-gallery__slides');
    if (!track) {
      return function () {};
    }

    var slides = Array.from(gallery.querySelectorAll('.custom-gallery__slide'));
    var thumbItems = Array.from(gallery.querySelectorAll('.custom-gallery__thumb-item'));
    var thumbBtns = Array.from(gallery.querySelectorAll('.custom-gallery__thumb-btn'));
    var thumbList = gallery.querySelector('.custom-gallery__thumbnails');
    var prevBtn = gallery.querySelector('.custom-gallery__arrow--prev');
    var nextBtn = gallery.querySelector('.custom-gallery__arrow--next');
    var viewport = gallery.querySelector('.custom-gallery__main');

    var total = slides.length;
    if (total === 0) {
      return function () {};
    }

    var current = slides.findIndex(function (s) {
      return s.classList.contains('is-active');
    });
    if (current < 0) current = 0;

    var prefersReduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var scrollBehavior = prefersReduce ? 'auto' : 'smooth';
    var programmatic = false;
    var scrollSyncTimer;
    var ro = null;

    var disposers = [];

    function addListener(el, type, fn, options) {
      if (!el || typeof el.addEventListener !== 'function') return;
      el.addEventListener(type, fn, options);
      disposers.push(function () {
        el.removeEventListener(type, fn);
      });
    }

    /** Let vertical swipes scroll the page; horizontal swipes stay on the gallery track. */
    function bindScrollAxisPassthrough(scroller) {
      if (!scroller) return;

      var startX = 0;
      var startY = 0;
      var axis = null;

      function resetAxis() {
        if (axis === 'y') scroller.style.overflowX = '';
        axis = null;
      }

      addListener(scroller, 'touchstart', function (e) {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        axis = null;
      }, { passive: true });

      addListener(scroller, 'touchmove', function (e) {
        if (e.touches.length !== 1) return;
        var dx = Math.abs(e.touches[0].clientX - startX);
        var dy = Math.abs(e.touches[0].clientY - startY);
        if (!axis && (dx > 8 || dy > 8)) {
          axis = dx > dy ? 'x' : 'y';
          if (axis === 'y') scroller.style.overflowX = 'hidden';
        }
      }, { passive: true });

      addListener(scroller, 'touchend', resetAxis, { passive: true });
      addListener(scroller, 'touchcancel', resetAxis, { passive: true });
    }

    /** Visible frame width — never use the scroll track (expands with all slides). */
    function slideWidth() {
      var el = viewport || track;
      var w = el.getBoundingClientRect().width;
      if (!w || !isFinite(w) || w <= 0) return 0;
      var cap = Math.max(window.innerWidth || 0, 4096) * 2;
      if (w > cap) return 0;
      return Math.round(w);
    }

    function setSlideWidths() {
      var w = slideWidth();
      if (w > 0) {
        track.style.setProperty('--cg-slide-w', w + 'px');
      } else {
        track.style.removeProperty('--cg-slide-w');
      }
    }

    function indexFromScroll() {
      var w = slideWidth();
      if (!w) return 0;
      var idx = Math.round(track.scrollLeft / w);
      return Math.max(0, Math.min(total - 1, idx));
    }

    function slideVideo(slide) {
      return slide ? slide.querySelector('video.custom-gallery__video') : null;
    }

    function resetVideo(video) {
      if (!video) return;
      video.pause();
      try {
        video.currentTime = 0;
      } catch (e) {}
    }

    function playSlideVideo(slide) {
      var video = slideVideo(slide);
      if (!video) return;
      video.muted = true;
      video.setAttribute('playsinline', '');
      if (video.preload === 'none') video.preload = 'metadata';
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {});
      }
    }

    function syncVideos(index) {
      slides.forEach(function (slide, i) {
        var video = slideVideo(slide);
        if (i === index) playSlideVideo(slide);
        else resetVideo(video);
      });
    }

    function applyActive(index) {
      index = Math.max(0, Math.min(total - 1, index));
      var prev = current;

      slides.forEach(function (slide, i) {
        var on = i === index;
        slide.classList.toggle('is-active', on);
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
      });

      thumbItems.forEach(function (item, i) {
        item.classList.toggle('is-active', i === index);
      });
      thumbBtns.forEach(function (btn, i) {
        if (i === index) btn.setAttribute('aria-current', 'true');
        else btn.removeAttribute('aria-current');
      });

      current = index;

      if (prev !== index) syncVideos(index);

      if (thumbItems[index] && thumbList) {
        var item = thumbItems[index];
        var scrollTarget = item.offsetLeft - thumbList.clientWidth / 2 + item.clientWidth / 2;
        thumbList.scrollTo({ left: scrollTarget, behavior: 'smooth' });
      }
    }

    function syncFromScroll() {
      applyActive(indexFromScroll());
    }

    function scheduleSyncFromScroll() {
      clearTimeout(scrollSyncTimer);
      scrollSyncTimer = setTimeout(syncFromScroll, 60);
    }

    function goTo(index) {
      index = ((index % total) + total) % total;
      var w = slideWidth();
      if (w <= 0) return;

      if (index === current && Math.abs(track.scrollLeft - index * w) < 4) return;

      programmatic = true;
      track.scrollTo({ left: index * w, behavior: scrollBehavior });
      applyActive(index);

      setTimeout(function () {
        programmatic = false;
      }, scrollBehavior === 'auto' ? 0 : 600);
    }

    function onScroll() {
      if (!programmatic) scheduleSyncFromScroll();
    }

    function onScrollEnd() {
      programmatic = false;
      syncFromScroll();
    }

    function onWinResize() {
      setSlideWidths();
      var w = slideWidth();
      if (w > 0) track.scrollTo({ left: current * w, behavior: 'auto' });
    }

    function alignScrollToCurrent(attemptsLeft) {
      setSlideWidths();
      var w = slideWidth();
      if (w > 0) {
        track.scrollLeft = current * w;
        return;
      }
      if (attemptsLeft > 0) {
        requestAnimationFrame(function () {
          alignScrollToCurrent(attemptsLeft - 1);
        });
      }
    }

    setSlideWidths();
    requestAnimationFrame(function () {
      alignScrollToCurrent(12);
    });
    setTimeout(function () {
      setSlideWidths();
      var w = slideWidth();
      if (w > 0) track.scrollLeft = current * w;
    }, 0);

    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(function () {
        setSlideWidths();
        var w = slideWidth();
        if (w > 0) track.scrollTo({ left: current * w, behavior: 'auto' });
      });
      ro.observe(viewport || track);
    } else {
      addListener(window, 'resize', onWinResize);
    }

    addListener(track, 'scroll', onScroll, { passive: true });
    addListener(track, 'scrollend', onScrollEnd);

    bindScrollAxisPassthrough(track);
    bindScrollAxisPassthrough(thumbList);

    if (prevBtn) {
      addListener(prevBtn, 'click', function () {
        goTo(current - 1);
      });
    }
    if (nextBtn) {
      addListener(nextBtn, 'click', function () {
        goTo(current + 1);
      });
    }

    thumbBtns.forEach(function (btn, i) {
      addListener(btn, 'click', function () {
        goTo(i);
      });
    });
    thumbItems.forEach(function (item, i) {
      addListener(item, 'keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goTo(i);
        }
      });
    });

    return function destroy() {
      slides.forEach(function (slide) {
        resetVideo(slideVideo(slide));
      });
      clearTimeout(scrollSyncTimer);
      disposers.forEach(function (d) {
        d();
      });
      disposers.length = 0;
      if (ro) {
        ro.disconnect();
        ro = null;
      }
    };
  }

  function resolveEl(el) {
    if (!el) return null;
    if (el.nodeType === 1) return el;
    if (typeof el === 'string') return document.querySelector(el);
    return null;
  }

  window.CustomGallery = {
    /**
     * Wire one gallery (scroll sync, arrows, thumbs). Safe to call again after destroy/reinit.
     * @param {HTMLElement|string} gallery — element or selector, e.g. '#CustomGallery-...'
     */
    init: function (gallery) {
      var el = resolveEl(gallery);
      if (!el || !el.classList || !el.classList.contains('custom-gallery')) return;

      if (el._cgDestroy) {
        el._cgDestroy();
        el._cgDestroy = null;
      }

      el.removeAttribute('data-ig-inited');
      el._cgDestroy = bindGallery(el);
    },

    /**
     * Remove listeners / observers for that gallery node.
     * @param {HTMLElement|string} gallery
     */
    destroy: function (gallery) {
      var el = resolveEl(gallery);
      if (!el || !el._cgDestroy) return;
      el._cgDestroy();
      el._cgDestroy = null;
    },

    /**
     * destroy + init
     * @param {HTMLElement|string} gallery
     */
    reinit: function (gallery) {
      this.init(gallery);
    },

    /**
     * Init every `.custom-gallery` under root (default: document).
     * @param {ParentNode} [root]
     */
    initAll: function (root) {
      var scope = root && root.querySelectorAll ? root : document;
      var nodes = scope.querySelectorAll('.custom-gallery');
      for (var i = 0; i < nodes.length; i++) {
        this.init(nodes[i]);
      }
    },

    /**
     * Same as initAll — alias for older injected scripts.
     * @param {ParentNode} [root]
     */
    reinitAll: function (root) {
      this.initAll(root);
    },

    /**
     * For HTML injected after page load: DOMContentLoaded has already fired, so do not use it.
     * Polls until the gallery node exists, then calls init (use if markup appears slightly after this script runs).
     * @param {HTMLElement|string} gallery
     * @param {{ maxAttempts?: number, intervalMs?: number }} [options]
     */
    bindWhenReady: function (gallery, options) {
      var self = this;
      var maxAttempts = (options && options.maxAttempts) || 150;
      var intervalMs = (options && options.intervalMs) || 50;
      var attempts = 0;

      function tryBind() {
        var el = resolveEl(gallery);
        if (!el || !el.classList || !el.classList.contains('custom-gallery')) return false;
        self.init(el);
        return true;
      }

      if (tryBind()) return true;

      var id = setInterval(function () {
        attempts++;
        if (tryBind() || attempts >= maxAttempts) clearInterval(id);
      }, intervalMs);

      return false;
    },
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('custom-gallery:attach', function (ev) {
      var d = ev && ev.detail;
      var el = d && (d.gallery || d.element || d.node);
      if (!el || !window.CustomGallery) return;
      window.CustomGallery.init(el);
    });
  }
})();
